/**
 * Gliederung der Immobilien-/Gebäude-Erfassung (Entscheidung E14, 18.08.2026).
 *
 * Die Gliederung ist **Spalte A der Fach-Excel**: die Hauptzeilen sind die
 * Abschnitte, die mit „==> Spezifizierung" beginnenden Zeilen die Schritte
 * darunter. Genau diese Struktur bildet `IMMO_GLIEDERUNG` ab.
 *
 * Zwei Ebenen, nicht drei: `SectionDef` kennt nur Abschnitt → Schritt, und
 * beide Ebenen sind hier belegt. Das **Gebäude** ist deshalb keine Ebene der
 * Liste mehr, sondern wird oberhalb des Verzeichnisses gewählt
 * (`Sidebar`-Prop `scopeSwitcher`). Die Liste bleibt damit unabhängig von der
 * Gebäudezahl kurz: 11 Abschnitte statt 1 + n × 16 + 1 Einträgen.
 *
 * Konvention, auf die sich Formular und Vorschau verlassen:
 * **`SectionDef.key` und `SubStepDef.key` sind Schritt-Schlüssel** aus
 * `buildImmoSteps`. Ein Klick auf eine Abschnittszeile führt also zum
 * Kopf-Schritt des Abschnitts, ein Klick auf einen Untereintrag zu dessen
 * Schritt — ohne dass die Sidebar Index-Arithmetik über die Schrittkette
 * betreiben müsste.
 */
import type { SectionColor, SectionDef, SubStepDef } from "../risikoanalyseStep/types";
import type { Gebaeude, ImmoStep, ImmoStepKind } from "./types";

/** Alle Schritt-Arten, die zu genau einem Gebäude gehören. */
export type GebaeudeStepKind = Exclude<ImmoStepKind, "anschrift" | "summary">;

/** Farbrotation der Gebäude-Abschnitte (Anschrift = orange, Summary = purple). */
const SECTION_COLORS: SectionColor[] = ["pillarOrange", "blue", "green", "black"];

export const titleForImmoStepKind = (k: ImmoStepKind): string => {
  switch (k) {
    case "anschrift": return "Risikoanschrift";
    case "gebaeudeart": return "Gebäudeart und Typ";
    // E14: hieß „Spezifizierung" — der Schritt erfragt Bauartklasse,
    // Geschosse, Baujahr und Nutzung, also die Bauart.
    case "spezifizierung": return "Bauart";
    case "dach": return "Dach";
    // Trägt seit dem 19.08.2026 auch die Spezifizierung der Einheiten und die
    // Kunst am Bau — der Titel bleibt trotzdem „Anzahl der Einheiten", weil die
    // Anzahl die erste und steuernde Angabe des Schritts ist.
    case "einheiten_anzahl": return "Anzahl der Einheiten";
    // E14: hieß „Branchen".
    case "branchen": return "Spezifizierung der Nutzung";
    case "aussenanlagen": return "Außenanlagen";
    case "bauausfuehrung": return "Bauausführung";
    case "sicherheit": return "Sicherheit";
    case "praevention": return "Schadenprävention";
    case "energie": return "Energie";
    case "umwelt": return "Umwelt";
    case "anbauten": return "Anbauten";
    case "eigentum": return "Eigentum & Rechte";
    case "beteiligte": return "Beteiligte am Objekt";
    case "gutachten": return "Gutachten";
    case "finanzierung": return "Finanzierung / Sicherungsrechte";
    case "versicherungsbedarf": return "Versicherungsbedarf";
    case "unterlagen": return "Unterlagen";
    case "summary": return "Übersicht";
  }
};

export type ImmoGliederungGroup = {
  /** Stabiler Gruppenschlüssel (nicht der Schritt-Schlüssel). */
  key: string;
  /** Beschriftung der Abschnittszeile — Hauptzeile der Excel. */
  title: string;
  /** Erster Schritt des Abschnitts; die Abschnittszeile führt dorthin. */
  head: GebaeudeStepKind;
  /** Die „==> Spezifizierung"-Zeilen darunter. */
  sub: GebaeudeStepKind[];
};

/**
 * Die Gliederung je Gebäude. Reihenfolge und Zuordnung stammen 1:1 aus E14;
 * `sicherheit`/`energie`/`umwelt` stehen nicht in der xlsx, sondern in der
 * zweiten Spec („Risiko Bauausführung, Bauausstattung, Energie, Umwelt") und
 * liegen deshalb unter Bauausführung.
 */
export const IMMO_GLIEDERUNG: ImmoGliederungGroup[] = [
  {
    key: "gebaeudeart",
    title: "Gebäudeart und Typ",
    head: "gebaeudeart",
    // „Leerstand" war hier bis 19.08.2026 ein eigener Unterschritt und steht
    // jetzt am Ende von „Bauart".
    sub: ["spezifizierung"],
  },
  { key: "dach", title: "Dach", head: "dach", sub: [] },
  {
    key: "einheiten",
    title: "Anzahl der Einheiten",
    head: "einheiten_anzahl",
    // „Spezifizierung" (der Einheiten) und „Kunst" waren hier bis 19.08.2026
    // eigene Unterschritte und stehen jetzt im Kopf-Schritt selbst — die
    // Anzahl steuert, ob die Spezifizierung überhaupt erscheint, und das ließ
    // sich über eine Seitengrenze hinweg nicht zeigen.
    sub: ["branchen", "aussenanlagen"],
  },
  {
    key: "bauausfuehrung",
    title: "Bauausführung",
    head: "bauausfuehrung",
    // „Schadenprävention" steht zwischen „Sicherheit" und „Energie": die drei
    // Präventionsblöcke gehören fachlich zur Sicherheit, sind aber Systeme zur
    // Früherkennung statt Anlagen — im Sicherheits-Schritt hinter 28 Anlagen
    // wären sie nicht mehr zu sehen gewesen.
    sub: ["sicherheit", "praevention", "energie", "umwelt"],
  },
  { key: "anbauten", title: "Anbauten", head: "anbauten", sub: [] },
  { key: "eigentuemer", title: "Eigentümer", head: "eigentum", sub: ["beteiligte"] },
  { key: "gutachten", title: "Gutachten", head: "gutachten", sub: [] },
  {
    key: "finanzierung",
    title: "Finanzierung / Sicherungsrechte",
    head: "finanzierung",
    sub: [],
  },
  {
    key: "versicherungsbedarf",
    title: "Versicherungsbedarf",
    head: "versicherungsbedarf",
    sub: [],
  },
  { key: "unterlagen", title: "Unterlagen", head: "unterlagen", sub: [] },
];

/**
 * Die Schrittkette eines Gebäudes, flach — abgeleitet aus der Gliederung,
 * damit Kette und Verzeichnis nicht auseinanderlaufen können.
 */
export const GEBAEUDE_STEP_ORDER: GebaeudeStepKind[] = IMMO_GLIEDERUNG.flatMap((g) => [
  g.head,
  ...g.sub,
]);

/**
 * Nur bei gewerblicher Nutzung erfragt. Die Außenanlagen standen bis 10/2026
 * ebenfalls hier — Pflaster und Grünanlagen gibt es aber auch beim Wohnhaus
 * (Fachabstimmung 31.08.2026).
 */
const NUR_GEWERBLICH: GebaeudeStepKind[] = ["branchen"];

export const istGewerblich = (g: Pick<Gebaeude, "answers">): boolean => {
  const kategorie = (g.answers.kategorie as string) ?? "";
  return (
    kategorie === "Gewerbe (100%)" || kategorie === "Mischnutzung (Wohnen u. Gewerbe)"
  );
};

/** Schritt-Schlüssel eines Gebäude-Schritts. */
export const immoStepKey = (gebaeudeId: string, kind: GebaeudeStepKind): string =>
  `${gebaeudeId}-${kind}`;

/**
 * Die vollständige Schrittkette: Anschrift · je Gebäude die Gliederung ·
 * Zusammenfassung. Die Reihenfolge ist gegenüber dem Stand vor E14
 * unverändert; neu sind ausschließlich `gutachten` und `finanzierung`, die aus
 * dem Eigentümer-Bereich herausgelöst wurden (Answer-Keys bleiben gleich).
 */
export const buildImmoSteps = (gebaeude: Gebaeude[]): ImmoStep[] => {
  const list: ImmoStep[] = [
    { kind: "anschrift", key: "anschrift", title: titleForImmoStepKind("anschrift") },
  ];
  for (const g of gebaeude) {
    const gewerblich = istGewerblich(g);
    for (const kind of GEBAEUDE_STEP_ORDER) {
      if (!gewerblich && NUR_GEWERBLICH.includes(kind)) continue;
      list.push({
        kind,
        key: immoStepKey(g.id, kind),
        gebaeudeId: g.id,
        gebaeudeLabel: g.label,
        title: titleForImmoStepKind(kind),
      });
    }
  }
  list.push({ kind: "summary", key: "summary", title: "Übersicht & Abschluss" });
  return list;
};

export type BuildImmoSectionsArgs = {
  steps: ImmoStep[];
  gebaeude: Gebaeude[];
  /** Gebäude, dessen Abschnitte das Verzeichnis zeigt. */
  activeGebaeudeId: string | null | undefined;
};

/**
 * Das Inhaltsverzeichnis für **ein** Gebäude: Anschrift, die neun Abschnitte
 * der Gliederung, Zusammenfassung. Abschnitte ohne „==> Spezifizierung"-Zeilen
 * haben bewusst `subSteps: []` — ihre Abschnittszeile ist der Schritt.
 */
export const buildImmoSections = ({
  steps,
  gebaeude,
  activeGebaeudeId,
}: BuildImmoSectionsArgs): SectionDef[] => {
  const aktiv = gebaeude.find((g) => g.id === activeGebaeudeId) ?? gebaeude[0];
  const vorhanden = new Set(
    steps
      .filter(
        (s): s is Extract<ImmoStep, { gebaeudeId: string }> =>
          s.kind !== "anschrift" && s.kind !== "summary",
      )
      .filter((s) => s.gebaeudeId === aktiv?.id)
      .map((s) => s.kind),
  );

  const gebaeudeSections: SectionDef[] = aktiv
    ? IMMO_GLIEDERUNG.filter((group) => vorhanden.has(group.head)).map((group, idx) => {
        const subSteps: SubStepDef[] = group.sub
          .filter((kind) => vorhanden.has(kind))
          .map((kind) => ({
            key: immoStepKey(aktiv.id, kind),
            title: titleForImmoStepKind(kind),
            questions: [],
          }));
        return {
          key: immoStepKey(aktiv.id, group.head),
          title: group.title,
          color: SECTION_COLORS[idx % SECTION_COLORS.length],
          subSteps,
        };
      })
    : [];

  return [
    { key: "anschrift", title: "Anschrift", color: "orange", subSteps: [] },
    ...gebaeudeSections,
    { key: "summary", title: "Zusammenfassung", color: "purple", subSteps: [] },
  ];
};

/**
 * Position des Schritts `stepKey` im Verzeichnis. `subStepIdx === -1` heißt:
 * der Kopf-Schritt des Abschnitts ist der aktuelle — die Abschnittszeile
 * selbst ist markiert.
 */
export const locateInSections = (
  sections: SectionDef[],
  stepKey: string | undefined,
): { sectionIdx: number; subStepIdx: number } => {
  if (stepKey) {
    for (let i = 0; i < sections.length; i += 1) {
      if (sections[i].key === stepKey) return { sectionIdx: i, subStepIdx: -1 };
      const j = sections[i].subSteps.findIndex((s) => s.key === stepKey);
      if (j >= 0) return { sectionIdx: i, subStepIdx: j };
    }
  }
  // Der Schritt gehört zu einem anderen Gebäude als dem gezeigten — dann ist
  // kein Eintrag markiert (statt fälschlich der erste).
  return { sectionIdx: -1, subStepIdx: -1 };
};
